import { AudienceFilterItem } from '../../../services/audience-api.service';
import { ProviderDto } from '../../../services/providers-api.service';
import { AudienceCreateFormValue, LogicalOperator, RuleGroupType } from '../create/audience-create.component';
import { FilterBuildResult } from './audience.types';
import { calculateTimerange } from './audience-format';

// Conversione FilterItems/QueryComposition del backend <-> stato del form di creazione audience.
// Estratta da AudienceComponent (comportamento identico): le dipendenze che prima erano stato del
// componente (providerMap, lista paesi, etichette tradotte) arrivano come parametri.

export const FILTER_TYPES = {
  TIMERANGE: 'Timerange',
  SOCIODEMO: 'Sociodemo',
  GEO: 'Geo',
  CUSTOM_IMPORT: 'CustomIdList',
  GOAL: 'Goal',
  AUDIENCE: 'Audience',
  SOURCE: 'Entity',
  EXCLUDED_SOURCE: 'ExcludedEntity',
  EXCLUDED_CUSTOM_IMPORT: 'ExcludedCustomIdList',
  EXCLUDED_GOAL: 'ExcludedGoal',
  EXCLUDED_AUDIENCE: 'ExcludedAudience'
} as const;

export type RuleBlocks = AudienceCreateFormValue['ruleBlocks'];
type RuleGroup = { type: RuleGroupType; valueOperator: LogicalOperator; payload: string[] };
export type ProviderLookup = ReadonlyMap<string, ProviderDto>;

export function normalizeFilterType(value?: string): string {
  return (value || '').toString().toLowerCase();
}

export function normalizeFilterItems(value: unknown): AudienceFilterItem[] {
  if (Array.isArray(value)) {
    return value as AudienceFilterItem[];
  }
  if (typeof value === 'string' && value.trim().length) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function coerceFilterValueToArray(value: any): string[] {
  if (Array.isArray(value)) {
    return value
      .map(entry => (entry !== null && entry !== undefined ? entry.toString().trim() : ''))
      .filter(entry => entry.length > 0);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) {
      return [];
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map(entry => (entry !== null && entry !== undefined ? entry.toString().trim() : ''))
          .filter(entry => entry.length > 0);
      }
    } catch {
      // Fall back to using the raw trimmed string.
    }
    return [trimmed];
  }
  if (value !== null && value !== undefined) {
    const normalized = value.toString().trim();
    return normalized ? [normalized] : [];
  }
  return [];
}

export function extractEntityIdsFromFilterValue(value: any): string[] {
  // Handle array of entity objects with EntityType and EntityID
  if (Array.isArray(value)) {
    return value
      .map(entry => {
        if (entry && typeof entry === 'object' && entry.EntityID) {
          return entry.EntityID.toString().trim();
        }
        // Fallback: treat as plain ID
        return (entry !== null && entry !== undefined ? entry.toString().trim() : '');
      })
      .filter(id => id.length > 0);
  }
  // Handle single entity object {EntityID, EntityType} (not wrapped in array)
  if (value && typeof value === 'object' && value.EntityID) {
    const id = value.EntityID.toString().trim();
    return id ? [id] : [];
  }
  // Fallback to coerceFilterValueToArray for backward compatibility
  return coerceFilterValueToArray(value);
}

const OPEN_ENDED_MAX_AGE = 99;

export function normalizeAgeRange(min?: number, max?: number): string {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return 'All Ages';
  }
  // Il backend tratta maxAge >= 99 come "nessun limite superiore" (compose-query.ts): la fascia
  // aperta si legge come `N+` ('65+', o il legacy '55+'). Una fascia chiusa come 55-64 resta tale:
  // prima ogni minAge 55 diventava '55+' e al salvataggio si allargava a 55-99.
  if ((max as number) >= OPEN_ENDED_MAX_AGE) {
    return `${min}+`;
  }
  return `${min}-${max}`;
}

export function resolveAgeBounds(range: string): { min: number; max: number } | null {
  if (!range || range === 'All Ages') {
    return null;
  }
  const openEnded = /^(\d+)\+$/.exec(range);
  if (openEnded) {
    return { min: Number(openEnded[1]), max: OPEN_ENDED_MAX_AGE };
  }
  const [rawMin, rawMax] = range.split('-');
  const min = Number(rawMin?.replace(/\D/g, ''));
  const max = Number(rawMax?.replace(/\D/g, ''));
  if (Number.isFinite(min) && Number.isFinite(max)) {
    return { min, max };
  }
  return null;
}

export function mapFilterTypeToRuleGroup(filterType?: string | null): RuleGroupType | null {
  const normalized = (filterType || '').toLowerCase();
  if (!normalized) {
    return null;
  }
  if (normalized === FILTER_TYPES.CUSTOM_IMPORT.toLowerCase() ||
      normalized === FILTER_TYPES.EXCLUDED_CUSTOM_IMPORT.toLowerCase() ||
      normalized.includes('custom_import')) {
    return 'custom_import';
  }
  if (normalized === FILTER_TYPES.GOAL.toLowerCase() ||
      normalized === FILTER_TYPES.EXCLUDED_GOAL.toLowerCase() ||
      normalized.includes('goal')) {
    return 'goal';
  }
  if (normalized === FILTER_TYPES.AUDIENCE.toLowerCase() ||
      normalized === FILTER_TYPES.EXCLUDED_AUDIENCE.toLowerCase() ||
      normalized.includes('audience')) {
    return 'audience';
  }
  if (normalized === FILTER_TYPES.SOURCE.toLowerCase() ||
      normalized === FILTER_TYPES.EXCLUDED_SOURCE.toLowerCase() ||
      normalized.includes('source') ||
      normalized === 'entity') {
    return 'source';
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Publisher → provider
// ---------------------------------------------------------------------------------------------

/**
 * Returns the provider ID that unambiguously represents a publisher, or null if
 * the publisher cannot be resolved:
 * - If a provider exists with ID === publisherEntityId → return that provider ID
 * - If exactly one provider has PublisherID === publisherEntityId → return that provider ID
 * - Otherwise → return null (ambiguous or not found)
 */
export function resolvePublisherToProvider(providers: ProviderLookup | null | undefined, publisherEntityId: string): string | null {
  if (!publisherEntityId) return null;
  const pidLower = publisherEntityId.toLowerCase();

  // Condition A: a provider whose own ID equals the publisher ID
  const directMatch = providers?.get(publisherEntityId)
    ?? [...(providers?.values() ?? [])].find(
         p => p.ID?.toString().trim().toLowerCase() === pidLower
       );
  if (directMatch) return directMatch.ID;

  // Condition B: exactly one provider belongs to this publisher
  const children = [...(providers?.values() ?? [])].filter(
    p => p.PublisherID?.toString().trim().toLowerCase() === pidLower
  );
  return children.length === 1 ? children[0].ID : null;
}

/**
 * For SOURCE filter items that contain publisher-type entities, replaces the publisher
 * EntityID with the resolved provider ID (when unambiguously resolvable).
 * Unresolvable publishers are left unchanged and will appear as stale items in the form.
 */
export function normalizePublisherEntities(filterItems: AudienceFilterItem[], providers: ProviderLookup | null | undefined): AudienceFilterItem[] {
  return filterItems.map(item => {
    if (mapFilterTypeToRuleGroup(item.filterType) !== 'source') {
      return item;
    }
    const values = Array.isArray(item.filterValue) ? item.filterValue : [item.filterValue];
    const normalized = (values as any[]).map(v => {
      if (v?.EntityType?.toLowerCase() !== 'publisher') return v;
      const resolvedId = resolvePublisherToProvider(providers, (v.EntityID ?? '').toString());
      if (!resolvedId) return v;
      return { ...v, EntityID: resolvedId, EntityType: 'provider' };
    });
    return { ...item, filterValue: Array.isArray(item.filterValue) ? normalized : normalized[0] };
  });
}

/**
 * true se l'audience usa filtri che il nuovo frontend non sa ricostruire (tipi non supportati,
 * mix rule+interests, entity publisher non risolvibili a un provider).
 */
export function inferHasIncompatibleFilters(
  filterItems: AudienceFilterItem[],
  queryComposition: string | null | undefined,
  providers: ProviderLookup | null | undefined
): boolean {
  // Filter types natively supported by the new frontend as interests-based
  const interestTypes = [
    FILTER_TYPES.SOCIODEMO,
    FILTER_TYPES.GEO,
    FILTER_TYPES.TIMERANGE,
  ] as string[];

  // If FilterItems is empty (e.g. NULL in DB), fall back to parsing QueryComposition.
  // QueryComposition tokens have the format "FilterType#ID" (e.g. "Model#5 AND Model#9").
  if (!filterItems.length) {
    if (!queryComposition?.trim()) return false;
    const tokens = queryComposition.match(/([A-Za-z]+)#\d+/g) ?? [];
    const typesInQuery = [...new Set(tokens.map(t => t.split('#')[0]))];
    if (!typesInQuery.length) return false;
    const ruleCount = typesInQuery.filter(t => !!mapFilterTypeToRuleGroup(t)).length;
    const interestCount = typesInQuery.filter(t => interestTypes.includes(t)).length;
    const unsupportedCount = typesInQuery.filter(t => !mapFilterTypeToRuleGroup(t) && !interestTypes.includes(t)).length;
    if (unsupportedCount > 0) return true;
    if (ruleCount > 0 && interestCount > 0) return true;
    return false;
  }

  const ruleItems: AudienceFilterItem[] = [];
  const interestItems: AudienceFilterItem[] = [];

  for (const item of filterItems) {
    const isRule = !!mapFilterTypeToRuleGroup(item.filterType);
    const isInterest = interestTypes.includes(item.filterType ?? '');

    if (!isRule && !isInterest) return true;  // unsupported filter type
    if (isRule) ruleItems.push(item);
    if (isInterest) interestItems.push(item);
  }

  // Mixed rule + interests → not reconstructable in new frontend
  if (ruleItems.length > 0 && interestItems.length > 0) return true;

  // Entity filters with publisher-type entities → new frontend supports only providers.
  // Exception: allow if every publisher entity can be unambiguously resolved to a single
  // provider (either a provider whose ID equals the PublisherID, or the publisher has
  // exactly one child provider in providerMap).
  const publisherEntityIds: string[] = ruleItems
    .filter(item => (item.filterType ?? '').toLowerCase() === FILTER_TYPES.SOURCE.toLowerCase())
    .flatMap(item => {
      const values = Array.isArray(item.filterValue) ? item.filterValue : [item.filterValue];
      return (values as any[])
        .filter(v => v?.EntityType?.toLowerCase() === 'publisher')
        .map(v => (v.EntityID ?? '').toString().trim())
        .filter(id => id.length > 0);
    });

  if (!publisherEntityIds.length) return false;

  // Check if every publisher can be unambiguously mapped to a single provider
  const allResolvable = publisherEntityIds.every(pid => resolvePublisherToProvider(providers, pid) !== null);
  return !allResolvable;
}

// ---------------------------------------------------------------------------------------------
// Backend → form (edit / clone)
// ---------------------------------------------------------------------------------------------

export function extractSociodemoFromFilters(filterItems: AudienceFilterItem[]): { ageRange: string[]; gender: string[] } {
  const fallback = { ageRange: ['All Ages'], gender: ['All Gender'] };
  const sociodemoFilters = filterItems.filter(item => normalizeFilterType(item.filterType) === 'sociodemo');

  if (sociodemoFilters.length === 0) {
    return fallback;
  }

  const ageRanges: string[] = [];
  const genders: string[] = [];

  sociodemoFilters.forEach(socioFilter => {
    if (!socioFilter.filterValue) return;

    const value = socioFilter.filterValue as { minAge?: number; maxAge?: number; gender?: 'M' | 'F' };

    if (value.minAge !== undefined || value.maxAge !== undefined) {
      const ageRange = normalizeAgeRange(value.minAge, value.maxAge);
      if (ageRange && !ageRanges.includes(ageRange)) {
        ageRanges.push(ageRange);
      }
    }

    if (value.gender) {
      const gender = value.gender === 'M' ? 'Male' : value.gender === 'F' ? 'Female' : null;
      if (gender && !genders.includes(gender)) {
        genders.push(gender);
      }
    }
  });

  // Se non abbiamo trovato valori, ritorna i default
  return {
    ageRange: ageRanges.length > 0 ? ageRanges : ['All Ages'],
    gender: genders.length > 0 ? genders : ['All Gender']
  };
}

export function extractLocationFromFilters(
  filterItems: AudienceFilterItem[],
  resolveCountryName: (countryCode: string) => string
): { location: string; regions: string[] } {
  const geo = filterItems.find(item => normalizeFilterType(item.filterType) === 'geo');
  if (!geo || !geo.filterValue) {
    return { location: 'All Locations', regions: [] };
  }
  const value = geo.filterValue as { country?: string; regions?: string[] };
  const countryCode = value.country ?? 'All Locations';
  const location = countryCode === 'IT' ? 'Italy' : resolveCountryName(countryCode);

  // For Italy:
  // - if backend omits regions or sends an empty array, treat it as the UI-level "All Regions" selection
  // - if backend sends explicit regions (even all of them), preserve that explicit selection
  let regions: string[] = [];
  if (countryCode === 'IT') {
    if (!value.regions || (Array.isArray(value.regions) && value.regions.length === 0)) {
      regions = ['All Regions'];
    } else if (Array.isArray(value.regions)) {
      regions = value.regions.filter(region => !!region);
    }
  } else {
    regions = Array.isArray(value.regions) ? value.regions.filter(region => !!region) : [];
  }

  return {
    location,
    regions
  };
}

export function rebuildRuleBlocksFromFilters(
  filterItems: AudienceFilterItem[],
  queryComposition?: string | null
): RuleBlocks {
  const parsedBlocks = rebuildRuleBlocksFromQuery(filterItems, queryComposition);
  if (parsedBlocks.length) {
    return parsedBlocks;
  }
  return rebuildRuleBlocksFallback(filterItems);
}

export function rebuildRuleBlocksFromQuery(
  filterItems: AudienceFilterItem[],
  queryComposition?: string | null
): RuleBlocks {
  if (!queryComposition || !queryComposition.trim()) {
    return [];
  }

  const tokenMap = buildFilterTokenMap(filterItems);
  if (!tokenMap.size) {
    return [];
  }

  const blockExpressions = extractBlockExpressions(queryComposition);
  if (!blockExpressions.length) {
    return [];
  }

  const blocks: RuleBlocks = [];
  blockExpressions.forEach(block => {
    const groups = buildGroupsFromExpression(block.expression, tokenMap);
    if (!groups.length) {
      return;
    }
    // If all groups in the block are excluded-type and the QueryComposition operator
    // is AND (old platform sometimes omits AND NOT), force the block operator to AND NOT.
    const allExcluded = groups.every(g => g.valueOperator === 'AND NOT');
    const blockOperator = allExcluded && (!block.operator || block.operator === 'AND')
      ? 'AND NOT'
      : normalizeBlockOperator(block.operator);
    blocks.push({ operator: blockOperator, groups });
  });
  return blocks;
}

export function rebuildRuleBlocksFallback(filterItems: AudienceFilterItem[]): RuleBlocks {
  if (!Array.isArray(filterItems) || !filterItems.length) {
    return [];
  }
  const customIds = new Set<string>();
  const goalIds = new Set<string>();
  const audienceIds = new Set<string>();
  const entityIDs = new Set<string>();
  const excludedEntityIDs = new Set<string>();
  const excludedCustomIds = new Set<string>();
  const excludedGoalIds = new Set<string>();
  const excludedAudienceIds = new Set<string>();

  filterItems.forEach(item => {
    const normalizedType = normalizeFilterType(item.filterType);

    // Special handling for SOURCE filters: extract EntityID from entity objects
    if (normalizedType === FILTER_TYPES.SOURCE.toLowerCase()) {
      const values = extractEntityIdsFromFilterValue(item.filterValue);
      values.forEach(value => entityIDs.add(value));
      return;
    }
    if (normalizedType === FILTER_TYPES.EXCLUDED_SOURCE.toLowerCase()) {
      const values = extractEntityIdsFromFilterValue(item.filterValue);
      values.forEach(value => excludedEntityIDs.add(value));
      return;
    }

    const values = coerceFilterValueToArray(item.filterValue);
    if (!values.length) {
      return;
    }

    // Handle include filters
    if (normalizedType === FILTER_TYPES.CUSTOM_IMPORT.toLowerCase()) {
      values.forEach(value => customIds.add(value));
    } else if (normalizedType === FILTER_TYPES.GOAL.toLowerCase()) {
      values.forEach(value => goalIds.add(value));
    } else if (normalizedType === FILTER_TYPES.AUDIENCE.toLowerCase()) {
      values.forEach(value => audienceIds.add(value));
    }
    // Handle exclude filters
    else if (normalizedType === FILTER_TYPES.EXCLUDED_CUSTOM_IMPORT.toLowerCase()) {
      values.forEach(value => excludedCustomIds.add(value));
    } else if (normalizedType === FILTER_TYPES.EXCLUDED_GOAL.toLowerCase()) {
      values.forEach(value => excludedGoalIds.add(value));
    } else if (normalizedType === FILTER_TYPES.EXCLUDED_AUDIENCE.toLowerCase()) {
      values.forEach(value => excludedAudienceIds.add(value));
    }
  });

  const groups: RuleGroup[] = [];

  // Include groups with AND operator
  if (customIds.size) {
    groups.push({ type: 'custom_import', valueOperator: 'AND', payload: Array.from(customIds) });
  }
  if (goalIds.size) {
    groups.push({ type: 'goal', valueOperator: 'AND', payload: Array.from(goalIds) });
  }
  if (audienceIds.size) {
    groups.push({ type: 'audience', valueOperator: 'AND', payload: Array.from(audienceIds) });
  }
  if (entityIDs.size) {
    groups.push({ type: 'source', valueOperator: 'AND', payload: Array.from(entityIDs) });
  }

  // Exclude groups with AND NOT operator (will be converted back to Exclude on submit)
  if (excludedCustomIds.size) {
    groups.push({ type: 'custom_import', valueOperator: 'AND NOT', payload: Array.from(excludedCustomIds) });
  }
  if (excludedGoalIds.size) {
    groups.push({ type: 'goal', valueOperator: 'AND NOT', payload: Array.from(excludedGoalIds) });
  }
  if (excludedAudienceIds.size) {
    groups.push({ type: 'audience', valueOperator: 'AND NOT', payload: Array.from(excludedAudienceIds) });
  }
  if (excludedEntityIDs.size) {
    groups.push({ type: 'source', valueOperator: 'AND NOT', payload: Array.from(excludedEntityIDs) });
  }

  return groups.length ? [{ operator: 'AND', groups }] : [];
}

function buildFilterTokenMap(filterItems: AudienceFilterItem[]): Map<string, AudienceFilterItem> {
  const map = new Map<string, AudienceFilterItem>();
  filterItems.forEach(item => {
    if (item?.ID == null || !item.filterType) {
      return;
    }
    const token = `${item.filterType}#${item.ID}`;
    map.set(token, item);
  });
  return map;
}

export function extractBlockExpressions(query: string): Array<{ expression: string; operator?: string }> {
  const trimmed = query.trim();
  if (!trimmed) {
    return [];
  }

  const blocks: Array<{ expression: string; operator?: string }> = [];
  let depth = 0;
  let buffer = '';
  let current = '';
  let pendingOperator: string | undefined;

  const flushOperator = () => {
    const match = buffer.match(/(AND\s+NOT|OR|AND|NOT)\s*$/i);
    pendingOperator = match ? match[1].toUpperCase() : undefined;
    buffer = '';
  };

  for (let i = 0; i < trimmed.length; i++) {
    const char = trimmed[i];
    if (char === '(') {
      if (depth === 0) {
        flushOperator();
      } else {
        current += char;
      }
      depth++;
    } else if (char === ')') {
      depth--;
      if (depth === 0) {
        blocks.push({ expression: current.trim(), operator: pendingOperator });
        current = '';
      } else if (depth > 0) {
        current += char;
      }
    } else {
      if (depth > 0) {
        current += char;
      } else {
        buffer += char;
      }
    }
  }

  if (!blocks.length && trimmed.length) {
    blocks.push({ expression: trimmed, operator: undefined });
  }
  return blocks;
}

function buildGroupsFromExpression(expression: string, tokenMap: Map<string, AudienceFilterItem>): RuleGroup[] {
  const groups: RuleGroup[] = [];
  const tokenRegex = /((?:AND\s+NOT|AND|OR|NOT)\s+)?([A-Za-z_]+#\d+)/gi;
  let match: RegExpExecArray | null;
  let isFirst = true;

  while ((match = tokenRegex.exec(expression)) !== null) {
    const operator = (match[1]?.trim() || 'AND').toUpperCase();
    const token = match[2];
    const filterItem = tokenMap.get(token);
    if (!filterItem) {
      continue;
    }
    const groupType = mapFilterTypeToRuleGroup(filterItem.filterType);
    if (!groupType) {
      continue;
    }

    // Check if this is an exclude filter type
    const normalizedFilterType = normalizeFilterType(filterItem.filterType);
    const isExcludeFilter = normalizedFilterType === FILTER_TYPES.EXCLUDED_CUSTOM_IMPORT.toLowerCase() ||
                            normalizedFilterType === FILTER_TYPES.EXCLUDED_GOAL.toLowerCase() ||
                            normalizedFilterType === FILTER_TYPES.EXCLUDED_AUDIENCE.toLowerCase() ||
                            normalizedFilterType === FILTER_TYPES.EXCLUDED_SOURCE.toLowerCase();

    // Use extractEntityIdsFromFilterValue for SOURCE filters to handle entity objects
    const payload = normalizedFilterType === FILTER_TYPES.SOURCE.toLowerCase() ||
                    normalizedFilterType === FILTER_TYPES.EXCLUDED_SOURCE.toLowerCase()
      ? extractEntityIdsFromFilterValue(filterItem.filterValue)
      : coerceFilterValueToArray(filterItem.filterValue);
    if (!payload.length) {
      continue;
    }

    // If it's an exclude filter, set operator to AND NOT
    const effectiveOperator = isExcludeFilter ? 'AND NOT' : normalizeGroupOperator(operator, isFirst);

    groups.push({
      type: groupType,
      valueOperator: effectiveOperator,
      payload
    });
    isFirst = false;
  }

  return groups;
}

function normalizeGroupOperator(operator: string, isFirst: boolean): LogicalOperator {
  if (isFirst) {
    return 'AND';
  }
  if (operator.includes('OR')) {
    return 'OR';
  }
  if (operator.includes('NOT')) {
    return 'AND NOT';
  }
  return 'AND';
}

function normalizeBlockOperator(operator?: string): LogicalOperator {
  const normalized = (operator || 'AND').toUpperCase();
  if (normalized.includes('OR')) {
    return 'OR';
  }
  if (normalized.includes('NOT')) {
    return 'AND NOT';
  }
  return 'AND';
}

// ---------------------------------------------------------------------------------------------
// Form → backend (create / update)
// ---------------------------------------------------------------------------------------------

export function buildInterestFilters(
  formValue: AudienceCreateFormValue,
  normalizedStart: string,
  normalizedEnd: string,
  deps: { allLocationsLabel: string; resolveCountryCode: (countryName: string) => string }
): FilterBuildResult {
  const items: AudienceFilterItem[] = [];
  const expressions: string[] = [];
  let nextId = 1;
  const addFilter = (type: string, value: any) => {
    const filter: AudienceFilterItem = { ID: nextId++, filterType: type, filterValue: value };
    items.push(filter);
    expressions.push(`${type}#${filter.ID}`);
  };

  const rawTimerange = calculateTimerange(normalizedStart, normalizedEnd);
  const timerange = Math.min(rawTimerange, 91);

  // Handle multiple age ranges and genders - create separate filters in OR
  const genderMap: Record<string, 'M' | 'F'> = { 'Male': 'M', 'Female': 'F' };
  // Filter out "All Ages", "All Gender", and "Unknown" values
  const selectedAges = (Array.isArray(formValue.ageRange) ? formValue.ageRange : [])
    .filter(age => age !== 'All Ages' && age !== 'Unknown');
  const selectedGenders = (Array.isArray(formValue.gender) ? formValue.gender : [])
    .filter(gender => gender !== 'All Gender' && gender !== 'Unknown');

  // Create a filter for each combination of age and gender
  const socioFilters: string[] = [];

  if (selectedAges.length > 0 || selectedGenders.length > 0) {
    // If only ages are selected (no gender filter)
    if (selectedAges.length > 0 && selectedGenders.length === 0) {
      selectedAges.forEach(ageRange => {
        const ageBounds = resolveAgeBounds(ageRange);
        if (ageBounds) {
          const socioValue: { minAge?: number; maxAge?: number } = {
            minAge: ageBounds.min,
            maxAge: ageBounds.max
          };
          const filter: AudienceFilterItem = { ID: nextId++, filterType: FILTER_TYPES.SOCIODEMO, filterValue: socioValue };
          items.push(filter);
          socioFilters.push(`${FILTER_TYPES.SOCIODEMO}#${filter.ID}`);
        }
      });
    }
    // If only genders are selected (no age filter)
    else if (selectedGenders.length > 0 && selectedAges.length === 0) {
      selectedGenders.forEach(gender => {
        const mappedGender = genderMap[gender];
        if (mappedGender) {
          const socioValue: { gender: 'M' | 'F' } = { gender: mappedGender };
          const filter: AudienceFilterItem = { ID: nextId++, filterType: FILTER_TYPES.SOCIODEMO, filterValue: socioValue };
          items.push(filter);
          socioFilters.push(`${FILTER_TYPES.SOCIODEMO}#${filter.ID}`);
        }
      });
    }
    // If both ages and genders are selected, create a filter for each combination
    else {
      selectedAges.forEach(ageRange => {
        selectedGenders.forEach(gender => {
          const ageBounds = resolveAgeBounds(ageRange);
          const mappedGender = genderMap[gender];
          const socioValue: { gender?: 'M' | 'F'; minAge?: number; maxAge?: number } = {};

          if (mappedGender) {
            socioValue.gender = mappedGender;
          }
          if (ageBounds) {
            socioValue.minAge = ageBounds.min;
            socioValue.maxAge = ageBounds.max;
          }

          if (Object.keys(socioValue).length > 0) {
            const filter: AudienceFilterItem = { ID: nextId++, filterType: FILTER_TYPES.SOCIODEMO, filterValue: socioValue };
            items.push(filter);
            socioFilters.push(`${FILTER_TYPES.SOCIODEMO}#${filter.ID}`);
          }
        });
      });
    }

    // Add socio filters to expressions with OR logic
    if (socioFilters.length > 0) {
      if (socioFilters.length === 1) {
        expressions.push(socioFilters[0]);
      } else {
        expressions.push(`(${socioFilters.join(' OR ')})`);
      }
    }
  }

  const fallbackLocation = deps.allLocationsLabel || 'All Locations';
  const normalizedLocation = (formValue.location || '').trim();
  const isAllLocation = !normalizedLocation || normalizedLocation === fallbackLocation || normalizedLocation === 'All Locations';
  if (!isAllLocation) {
    const regions = normalizedLocation === 'Italy' ? (formValue.regions || []) : [];
    const countryCode = normalizedLocation === 'Italy' ? 'IT' : deps.resolveCountryCode(normalizedLocation);

    // Build geo filter value
    const geoValue: { country: string; regions?: string[] } = { country: countryCode };

    // For Italy, omit regions only for the explicit UI-level "All Regions" selection.
    if (normalizedLocation === 'Italy' && regions.length > 0) {
      geoValue.regions = regions;
    } else if (normalizedLocation !== 'Italy') {
      geoValue.regions = regions;
    }

    addFilter(FILTER_TYPES.GEO, geoValue);
  }

  return {
    filterItems: items,
    queryComposition: expressions.join(' AND '),
    timerange,
    selectedSourceGroups: [],
    hasRuleFilters: true
  };
}

export function buildRuleBasedFilters(
  formValue: AudienceCreateFormValue,
  normalizedStart: string,
  normalizedEnd: string,
  providers: ProviderLookup | null | undefined
): FilterBuildResult {
  const items: AudienceFilterItem[] = [];
  let nextId = 1;
  const selectedSourceGroups = new Set<string>();
  const rawTimerange = calculateTimerange(normalizedStart, normalizedEnd);
  const timerange = Math.min(rawTimerange, 91);

  const addFilter = (type: string, value: any): string => {
    const filter: AudienceFilterItem = {
      ID: nextId++,
      filterType: type,
      filterValue: value
    };
    items.push(filter);
    return `${type}#${filter.ID}`;
  };

  const blockExpressions: Array<{ expression: string; operator?: string }> = [];

  const normalizedBlocks = Array.isArray(formValue.ruleBlocks) ? formValue.ruleBlocks : [];
  normalizedBlocks.forEach(block => {
    const groups = Array.isArray(block?.groups) ? block.groups : [];
    const isSingleGroupBlock = groups.length === 1;
    const blockIsExclude = isExcludeOperator(block?.operator);
    const groupEntries: Array<{ expression: string; operator?: string }> = [];
    groups.forEach(group => {
      const payload = normalizeRuleGroupPayload(group?.payload);
      if (!payload.length) {
        return;
      }

      // The UI currently stores AND NOT at block level, not between groups.
      // Apply it to the single group contained in that block.
      const isExclude = isExcludeOperator(group?.valueOperator) || (blockIsExclude && isSingleGroupBlock);
      // When AND NOT is used, convert to Exclude filter type and change operator to AND
      const effectiveOperator = isExclude ? 'AND' : group?.valueOperator;

      switch ((group?.type || 'custom_import') as RuleGroupType) {
        case 'custom_import': {
          const filterType = isExclude ? FILTER_TYPES.EXCLUDED_CUSTOM_IMPORT : FILTER_TYPES.CUSTOM_IMPORT;
          const token = addFilter(filterType, payload);
          groupEntries.push({ expression: token, operator: effectiveOperator });
          break;
        }
        case 'goal': {
          const filterType = isExclude ? FILTER_TYPES.EXCLUDED_GOAL : FILTER_TYPES.GOAL;
          const token = addFilter(filterType, payload);
          groupEntries.push({ expression: token, operator: effectiveOperator });
          break;
        }
        case 'audience': {
          const filterType = isExclude ? FILTER_TYPES.EXCLUDED_AUDIENCE : FILTER_TYPES.AUDIENCE;
          const token = addFilter(filterType, payload);
          groupEntries.push({ expression: token, operator: effectiveOperator });
          break;
        }
        case 'source': {
          payload.forEach(id => selectedSourceGroups.add(id));
          if (payload.length) {
            const filterType = isExclude ? FILTER_TYPES.EXCLUDED_SOURCE : FILTER_TYPES.SOURCE;
            // If the provider has ID === PublisherID, it represents the publisher itself → save as 'publisher'
            const entityObjects = payload.map(id => {
              const provider = providers?.get(id);
              const isPublisherProvider = !!provider?.PublisherID && provider.ID === provider.PublisherID;
              return { EntityType: isPublisherProvider ? 'publisher' : 'provider', EntityID: id };
            });
            const token = addFilter(filterType, entityObjects);
            groupEntries.push({ expression: token, operator: effectiveOperator });
          }
          break;
        }
        default:
          break;
      }
    });

    const combinedGroupExpression = combineExpressions(groupEntries);
    if (combinedGroupExpression) {
      // Ogni block è sempre tra parentesi per mantenere la precedenza
      const wrappedExpression = `(${combinedGroupExpression})`;
      blockExpressions.push({ expression: wrappedExpression, operator: block?.operator });
    }
  });

  return {
    filterItems: items,
    queryComposition: combineExpressions(blockExpressions),
    timerange,
    selectedSourceGroups: Array.from(selectedSourceGroups),
    hasRuleFilters: blockExpressions.length > 0 || selectedSourceGroups.size > 0
  };
}

export function ensureSourceFiltersAreSerialized(filterBuild: FilterBuildResult): void {
  if (!filterBuild?.selectedSourceGroups?.length) {
    return;
  }
  const existingSource = filterBuild.filterItems.some(item => {
    const normalizedType = normalizeFilterType(item.filterType);
    return normalizedType === FILTER_TYPES.SOURCE.toLowerCase() ||
      normalizedType === FILTER_TYPES.EXCLUDED_SOURCE.toLowerCase();
  });
  if (existingSource) {
    return;
  }
  const existingIds = filterBuild.filterItems
    .map(item => Number(item.ID) || 0)
    .filter(id => Number.isFinite(id));
  const nextId = existingIds.length ? Math.max(...existingIds) + 1 : 1;

  // Transform source group IDs to entity objects with EntityType: 'provider' and EntityID
  const entityObjects = filterBuild.selectedSourceGroups.map(id => ({
    EntityType: 'provider',
    EntityID: id
  }));

  const filter: AudienceFilterItem = {
    ID: nextId,
    filterType: FILTER_TYPES.SOURCE,
    filterValue: entityObjects
  };
  filterBuild.filterItems.push(filter);
  const expression = `${FILTER_TYPES.SOURCE}#${filter.ID}`;
  filterBuild.queryComposition = filterBuild.queryComposition
    ? `${filterBuild.queryComposition} AND (${expression})`
    : `(${expression})`;
}

export function normalizeRuleGroupPayload(payload: unknown): string[] {
  if (Array.isArray(payload)) {
    const flattened = payload.reduce<any[]>((acc, entry) => {
      if (Array.isArray(entry)) {
        acc.push(...entry);
      } else if (entry !== null && entry !== undefined) {
        acc.push(entry);
      }
      return acc;
    }, []);
    return coerceFilterValueToArray(flattened);
  }
  return coerceFilterValueToArray(payload);
}

export function combineExpressions(entries: Array<{ expression: string; operator?: string }>): string {
  return entries.reduce((acc, entry) => {
    const expression = entry.expression?.trim();
    if (!expression) {
      return acc;
    }
    if (!acc) {
      return expression;
    }
    const operator = normalizeLogicalOperator(entry.operator);
    return `${acc} ${operator} ${expression}`;
  }, '');
}

export function normalizeLogicalOperator(operator?: string | null): string {
  const normalized = (operator || 'AND').toString().trim().toUpperCase();
  if (normalized === 'OR') {
    return 'OR';
  }
  if (normalized === 'NOT') {
    return 'AND NOT';
  }
  return 'AND';
}

export function isExcludeOperator(operator?: string | null): boolean {
  const normalized = (operator || '').toString().trim().toUpperCase();
  return normalized === 'AND NOT' || normalized === 'NOT';
}
